import {forwardRef, useImperativeHandle, useMemo, useRef} from "react";
import {useEmployeeRoles, useEmployees} from "../../hooks/team/employee-hooks";
import {useSelector} from "react-redux";
import {Modal, Tab, Table} from "@frostbyte-technologies/frostbyte-tailwind";
import LoadingSpinner from "../../components/loading-spinner";
import {SORT_BY_ALPHABETICAL} from "./scheduler";
import {EmployeeSequences} from "../../utils/request-helpers/employees/employee-requests";

export const CustomOrderModal = forwardRef(({handleAlphabeticalOrder, calendarRefresh}, ref) => {
  const modalRef = useRef(null);

  const locations = useSelector((state) => state.scheduling.locationArr);

  const shopLocationId = useSelector((state) => state.shop.location.ID);

  const selectedLocations = useMemo(() => {
    if (!locations) {
      return [shopLocationId];
    }

    return locations;
  }, [locations, shopLocationId]);

  const locationId = shopLocationId;

  const {
    data: employees = [],
    isLoading: employeesLoading,
    refetch: employeesRefetch,
  } = useEmployees(locationId);

  const {data: roles = [], isLoading: rolesLoading, refetch: rolesRefetch} = useEmployeeRoles();

  const sortedEmployees = useMemo(() => {
    const locationSet = new Set(selectedLocations.map(Number));

    return employees
      .filter((emp) => {
        const empLoc = parseInt(emp.LOCATION_ID);
        const allLocs = emp.ALL_LOCATION_IDS || [];

        const isInSelectedLocation =
          locationSet.has(empLoc) || allLocs.some((locId) => locationSet.has(locId));

        return isInSelectedLocation && emp.ACTIVE === 1;
      })
      .sort((a, b) => a.SEQ - b.SEQ);
  }, [employees, selectedLocations]);

  const sortedRoles = useMemo(() => {
    return roles.filter((role) => !role.DATE_ARCHIVED).sort((a, b) => a.SEQ - b.SEQ);
  }, [roles]);

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current.open();

      employeesRefetch();

      rolesRefetch();
    },
  }));

  const handleModalClose = () => {
    modalRef.current.close();
  };

  const saveCustomOrder = async () => {
    const empSeq = EmployeeSequences.createEmployee({
      EMPLOYEES: sortedEmployees.map((emp) => ({
        SEQ: emp.SEQ,
        ID: emp.ID,
      })),
      LOCATION_ID: locationId,
    });

    const rolesSeq = EmployeeSequences.createRole({
      ROLES: sortedRoles.map((role) => ({
        SEQ: role.SEQ,
        ID: role.ID,
      })),
    });

    await Promise.all([empSeq, rolesSeq]);

    handleAlphabeticalOrder(SORT_BY_ALPHABETICAL.CUSTOM);

    await calendarRefresh();

    handleModalClose();
  };

  const displayRolesTab = () => (
    <Table columns={[{label: "Name", value: "NAME"}]} data={sortedRoles} draggable />
  );

  const displayEmployeesTab = () => (
    <Table columns={[{label: "Name", value: "FULL_NAME"}]} data={sortedEmployees} draggable />
  );

  const displayTabContent = (id) => {
    if (employeesLoading || rolesLoading) {
      return (
        <div className={"m-12"}>
          <LoadingSpinner />
        </div>
      );
    }

    if (id === "employees") {
      return displayEmployeesTab();
    }

    return displayRolesTab();
  };

  return (
    <Modal
      ref={modalRef}
      closeLabel={"Close"}
      label={"Custom Order"}
      description={"Drag and drop to choose your desired order"}
      large
      full
      buttonLabel={"Save"}
      buttonOnClick={() => saveCustomOrder()}
      onClose={() => handleModalClose()}
    >
      <Tab
        data={[
          {label: "Roles", id: "roles"},
          {label: "Employees", id: "employees"},
        ]}
      >
        {(id) => displayTabContent(id)}
      </Tab>
    </Modal>
  );
});
